import { useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useHasRole } from '@/components/auth/RoleGuard';
import { SubscriptionInactive } from '@/components/subscription/SubscriptionInactive';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { Sheet, SheetContent } from '@/components/ui/sheet';

export function MainLayout() {
  const { isAuthenticated, selectedClinic, isLoading } = useAuth();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isActive, cancelAtPeriodEnd, endDate } = useSubscription();
  const isAdmin = useHasRole('ADMIN');
  const { pathname } = useLocation();
  // Assinatura inativa: única saída é /settings (ADMIN) — as demais rotas
  // mostram a tela de assinatura inativa em vez de páginas sem feature.
  const blocked = !isActive && !(isAdmin && pathname.startsWith('/settings'));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!selectedClinic) {
    return <Navigate to="/select-clinic" replace />;
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop sidebar */}
      {!isMobile && <Sidebar />}

      {/* Mobile sidebar (Sheet overlay) */}
      {isMobile && (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="p-0 w-60">
            <Sidebar mobile onNavigate={() => setSidebarOpen(false)} />
          </SheetContent>
        </Sheet>
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        <TopBar onMenuClick={isMobile ? () => setSidebarOpen(true) : undefined} />
        <main className="relative flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin">
          {cancelAtPeriodEnd && endDate && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
              A assinatura foi cancelada. O acesso segue até {new Date(endDate).toLocaleDateString('pt-BR')}.
            </div>
          )}
          {blocked ? <SubscriptionInactive /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
